import { z } from "zod";
import { isValidWebsiteInput } from "./website-metadata";

export const emailSchema = z
	.string()
	.trim()
	.min(1, "Email is required")
	.email("Enter a valid email address");

export const passwordSchema = z
	.string()
	.min(8, "Password must be at least 8 characters")
	.max(128, "Password is too long");

export const currentPasswordSchema = z
	.string()
	.min(1, "Current password is required");

export const websiteSchema = z
	.string()
	.trim()
	.min(3, "Website is required")
	.max(300, "Website is too long")
	.refine(isValidWebsiteInput, "Enter a valid website, like example.com");

export const restrictedStripeKeySchema = z
	.string()
	.trim()
	.min(1, "Stripe API key is required")
	.refine(
		(value) => value.startsWith("rk_live_") || value.startsWith("rk_test_"),
		"Use a restricted key (starts with rk_live_ or rk_test_)",
	);

export const optionalStripeKeySchema = z
	.string()
	.trim()
	.optional()
	.refine(
		(value) =>
			!value || value.startsWith("rk_live_") || value.startsWith("rk_test_"),
		"Use a restricted key (starts with rk_live_ or rk_test_)",
	);

export const displayNameSchema = z
	.string()
	.trim()
	.min(2, "Name must be at least 2 characters")
	.max(120, "Name is too long");

export const xHandleSchema = z
	.string()
	.trim()
	.max(32, "X handle is too long")
	.regex(/^@?[A-Za-z0-9_]{0,15}$/, "Handle must be letters, numbers, or _")
	.optional()
	.or(z.literal(""));

/**
 * Resolves an X/Twitter handle to its avatar via unavatar.io (a free image
 * proxy). Returns undefined for missing/invalid handles. Hotlinked directly as
 * an <img src>; callers should keep their own initials fallback for when the
 * proxy can't resolve the handle.
 */
export function xAvatarUrl(handle?: string | null): string | undefined {
	if (!handle) return undefined;
	const normalized = handle.trim().replace(/^@+/, "");
	if (!/^[A-Za-z0-9_]{1,15}$/.test(normalized)) return undefined;
	return `https://unavatar.io/x/${normalized}`;
}

export const taglineSchema = z
	.string()
	.trim()
	.max(160, "Tagline is too long")
	.optional()
	.or(z.literal(""));

export const founderStoryFieldSchema = z
	.string()
	.trim()
	.max(600, "Keep it under 600 characters")
	.optional()
	.or(z.literal(""));

export function passwordsMatch<T extends { password: string; confirm: string }>(
	data: T,
	ctx: z.RefinementCtx,
) {
	if (data.password !== data.confirm) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: "Passwords do not match",
			path: ["confirm"],
		});
	}
}
